import {BaseCpFormModel, ControlProps} from "../../../../models/form/base-cp-form.model";
import {UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {JobTemplateModel} from "../../../../models/job_template/job-template.model";
import DurationUnit, {getMillisByDurationUnit} from "../../../../enums/DurationUnit";
import {SelectItem} from "primeng/api";
import Constants from "../../../../Constants";
import {Observable, Subscription} from "rxjs";
import {takeUntil} from "rxjs/operators";

export class JobRoutineTimeFormModel extends BaseCpFormModel<JobRoutineTimeFormModel, JobTemplateModel> {

  static readonly DEFAULT_DURATION_UNIT: DurationUnit = DurationUnit.MINUTE;

  static readonly durationUnits: SelectItem[] = Constants.DROPDOWNS.types.durationUnits;

  //region FIELDS
  static readonly TIME_EXECUTION_DURATION_AMOUNT = 'workTimeDurationAmount';
  static readonly TIME_EXECUTION_DURATION_UNIT = 'workTimeDurationUnit';
  static readonly TIME_STAND_STILL_DURATION_AMOUNT = 'standStillDurationAmount';
  static readonly TIME_STAND_STILL_DURATION_UNIT = 'standStillDurationUnit';
  static readonly TIME_PLANNED_IMPL_DURATION_AMOUNT = 'plannedImplDurationAmount';
  static readonly TIME_PLANNED_IMPL_DURATION_UNIT = 'plannedImplDurationUnit';
  static readonly TIME_IS_IDENTICAL = 'isIdentical';
  static readonly TIME_IS_IDENTICAL_PREV_VALUE = 'isIdenticalPrevValue';
  static readonly TIME_STAND_STILL = 'standstill';
  //endregion FIELDS

  //region controls
  workTimeDurationAmountControl: UntypedFormControl;
  workTimeDurationUnitControl: UntypedFormControl;
  plannedImplDurationAmountControl: UntypedFormControl;
  plannedImplDurationUnitControl: UntypedFormControl;
  isIdenticalControl: UntypedFormControl;
  isIdenticalPrevValueControl: UntypedFormControl;
  standstillControl: UntypedFormControl;
  //endregion controls

  defaultDurationUnit: SelectItem = JobRoutineTimeFormModel.getDefaultDurationUnit();

  form: UntypedFormGroup;

  asFormGroup(): UntypedFormGroup {

    this.form.addControl(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT, this.workTimeDurationAmountControl);
    this.form.addControl(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_UNIT, this.workTimeDurationUnitControl);
    this.form.addControl(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_AMOUNT, this.plannedImplDurationAmountControl);
    this.form.addControl(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_UNIT, this.plannedImplDurationUnitControl);
    this.form.addControl(JobRoutineTimeFormModel.TIME_IS_IDENTICAL, this.isIdenticalControl);
    this.form.addControl(JobRoutineTimeFormModel.TIME_IS_IDENTICAL_PREV_VALUE, this.isIdenticalPrevValueControl);
    this.form.addControl(JobRoutineTimeFormModel.TIME_STAND_STILL, this.standstillControl);

    return this.form;
  }

  asModel(formGroup: UntypedFormGroup, model: JobTemplateModel): JobTemplateModel {
    model[JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT] = formGroup.get(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT).value;
    model[JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_UNIT] = formGroup.get(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_UNIT).value;
    model[JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_AMOUNT] = formGroup.get(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_AMOUNT).value;
    model[JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_UNIT] = formGroup.get(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_UNIT).value;
    model[JobRoutineTimeFormModel.TIME_IS_IDENTICAL] = formGroup.get(JobRoutineTimeFormModel.TIME_IS_IDENTICAL).value;
    model[JobRoutineTimeFormModel.TIME_STAND_STILL] = formGroup.get(JobRoutineTimeFormModel.TIME_STAND_STILL).value;

    return model;
  }

  createForm(formModel: JobRoutineTimeFormModel, model: JobTemplateModel): JobRoutineTimeFormModel {
    const executionDuration = JobRoutineTimeFormModel.getDurationUnitAndAmount(model?.plannedWorkingTime);
    const executionDurationAmountProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_AMOUNT);
    executionDurationAmountProps.withDefaultValue(null);
    executionDurationAmountProps.withValue(model && model.plannedWorkingTime != null && !model.isCreate? executionDuration.time : null);
    this.initControl(formModel, model, executionDurationAmountProps);

    const executionDurationUnitProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_EXECUTION_DURATION_UNIT);
    executionDurationUnitProps.withDefaultValue(this.defaultDurationUnit);
    executionDurationUnitProps.withValue(model && model.plannedWorkingTime && !model.isCreate ? executionDuration.unit : this.defaultDurationUnit);
    this.initControl(formModel, model, executionDurationUnitProps);

    const plannedImplDuration = JobRoutineTimeFormModel.getDurationUnitAndAmount(model?.plannedImplDuration);
    const plannedImplDurationAmountProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_AMOUNT);
    plannedImplDurationAmountProps.withDefaultValue(null );
    plannedImplDurationAmountProps.withValue(model && model.plannedImplDuration && !model.isCreate ? plannedImplDuration.time : null);
    this.initControl(formModel, model, plannedImplDurationAmountProps);

    const plannedImplDurationUnitProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_PLANNED_IMPL_DURATION_UNIT);
    plannedImplDurationUnitProps.withDefaultValue(this.defaultDurationUnit);
    plannedImplDurationUnitProps.withValue(model && model.plannedImplDuration && !model.isCreate ? plannedImplDuration.unit : this.defaultDurationUnit);
    this.initControl(formModel, model, plannedImplDurationUnitProps);

    const timeIsIdenticalProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_IS_IDENTICAL);
    timeIsIdenticalProps.withDefaultValue(true);
    timeIsIdenticalProps.withValue(model && model.isIdentical !== null && !model.isCreate ? model.isIdentical : true);
    this.initControl(formModel, model, timeIsIdenticalProps);

    const timeIsIdenticalPrevValueProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_IS_IDENTICAL_PREV_VALUE);
    timeIsIdenticalPrevValueProps.withDefaultValue(model && model.isIdentical && !model.isCreate ? model.isIdentical : true);
    this.initControl(formModel, model, timeIsIdenticalPrevValueProps);

    const timeStandStillProps: ControlProps = ControlProps.of(JobRoutineTimeFormModel.TIME_STAND_STILL);
    timeStandStillProps.withDefaultValue(model && model.standstill && !model.isCreate ? model.standstill : true); //Default
    timeIsIdenticalProps.withValue(model && model.standstill && !model.isCreate ? model.standstill : true);
    this.initControl(formModel, model, timeStandStillProps);

    formModel.form = new UntypedFormGroup({});

    return formModel;
  }

  //region setup value changes
  setupTimeStandStillValueChanges(until$: Observable<any>): Subscription {
    return this.standstillControl.valueChanges
      .pipe(takeUntil(until$))
      .subscribe(v => {
      if (v) {
        this.isIdenticalControl.setValue(false);
      } else {
        this.isIdenticalControl.setValue(this.isIdenticalPrevValueControl.value);
      }
    });
  }

  setupTimeIsIdenticalValueChanges(until$: Observable<any>): Subscription {
    return this.isIdenticalControl.valueChanges
      .pipe(takeUntil(until$))
      .subscribe(v => {
        this.isIdenticalPrevValueControl.setValue(v);
      });
  }
  //endregion setup value changes

  static getDefaultDurationUnit(): SelectItem {
    return this.durationUnits.find(e => e.value === JobRoutineTimeFormModel.DEFAULT_DURATION_UNIT);
  }

  getDurationMillis(field: string): number {
    const unit = this.form.get(field).value?.value;
    return getMillisByDurationUnit(unit);
  }

  static getDurationUnitAndAmount(millis: number): { time: number, unit: SelectItem } {
    if (millis === null) {
      return {
        time: null,
        unit: this.durationUnits.find(d => d.value === DurationUnit.MINUTE)
      };
    }

    if (millis === 0) {
      return {
        time: 0,
        unit: this.durationUnits.find(d => d.value === DurationUnit.MINUTE)
      };
    }

    const timeInMIN = (millis / 1000) / 60;
    if (timeInMIN % 60 !== 0) {
      return {
        time: timeInMIN,
        unit: this.durationUnits.find(d => d.value === DurationUnit.MINUTE)
      };
    }
    const timeInHour = timeInMIN / 60;
    if (timeInHour % 24 !== 0) {
      return {
        time: timeInHour,
        unit: this.durationUnits.find(d => d.value === DurationUnit.HOUR)
      };
    }
    const timeInDay = timeInHour / 24;
    if (timeInDay % 7 !== 0) {
      return {
        time: timeInDay,
        unit: this.durationUnits.find(d => d.value === DurationUnit.DAY)
      };
    }
    const timeInWeek = timeInDay / 7;
    if (timeInWeek % 4 !== 0) {
      return {
        time: timeInWeek,
        unit: this.durationUnits.find(d => d.value === DurationUnit.WEEK)
      };
    }
    const timeInMonth = timeInWeek / 4;
    if (timeInMonth % 12 !== 0) {
      return {
        time: timeInMonth,
        unit: this.durationUnits.find(d => d.value === DurationUnit.MONTH)
      };
    }

    return {
      time: timeInMonth / 12,
      unit: this.durationUnits.find(d => d.value === DurationUnit.YEAR)
    };
  }

}
